import theme, {
  createExhibitorTheme,
  defaultDarkAlertError,
  defaultDarkAlertInfo,
  defaultDarkAlertSuccess,
  defaultDarkAlertWarning,
  defaultDarkAttachmentsBackground,
  defaultDarkAutofillBackground,
  defaultDarkChipBackground,
  defaultDarkContrastEmailText,
  defaultDarkContrastEmailWrapper,
  defaultDarkContrastText,
  defaultDarkContrastTextWrapper,
  defaultDarkDisabledButtonBackground,
  defaultDarkDisabledButtonText,
  defaultDarkDisabledInputLabel,
  defaultDarkDisabledSecondaryText,
  defaultDarkDisabledText,
  defaultDarkEmailSeparator,
  defaultDarkIcon,
  defaultDarkLeftMenu,
  defaultDarkOtherElements,
  defaultDarkPaymentName,
  defaultDarkPlaceholder,
  defaultDarkReverseContrastText,
  defaultDarkSecondaryContrast,
  defaultDarkSecondaryElementsBackground,
  defaultDarkSeparator,
  defaultDarkTableDataRow,
  defaultDarkTableRowIcon,
  defaultDarkTableRowTitle,
  defaultDarkTooltipBackground,
  defaultDarkTooltipText,
  defaultLightAlertError,
  defaultLightAlertInfo,
  defaultLightAlertSuccess,
  defaultLightAlertWarning,
  defaultLightAttachmentsBackground,
  defaultLightAutofillBackground,
  defaultLightChipBackground,
  defaultLightContrastEmailText,
  defaultLightContrastEmailWrapper,
  defaultLightContrastText,
  defaultLightContrastTextWrapper,
  defaultLightDisabledButtonBackground,
  defaultLightDisabledButtonText,
  defaultLightDisabledInputLabel,
  defaultLightDisabledSecondaryText,
  defaultLightDisabledText,
  defaultLightEmailSeparator,
  defaultLightIcon,
  defaultLightLeftMenu,
  defaultLightEmailBackground,
  defaultLightOtherElements,
  defaultLightPaymentName,
  defaultLightPlaceholder,
  defaultLightReverseContrastText,
  defaultLightSecondaryContrast,
  defaultLightSecondaryElementsBackground,
  defaultLightSeparator,
  defaultLightTableDataRow,
  defaultLightTableRowIcon,
  defaultLightTableRowTitle,
  defaultLightTooltipBackground,
  defaultLightTooltipText,
} from "tools/theme";
import {BLACK_RGB, BLACK_WEIGHT, mixHexWithColor} from "tools/theme/helpers";
import {fade} from "@material-ui/core/styles/colorManipulator";

import {Branding} from "store/branding/branding.store";
import {Theme} from "@evennt-tech/api-v2-core";
import {useMemo} from "react";

export const generateThemeFromBranding = (branding: Branding) => {
  if (!branding?.primaryColor) {
    return theme;
  }
  const isDark = branding.theme === Theme.DARK;
  return createExhibitorTheme({
    primaryColor: branding.primaryColor,
    secondaryColor: isDark
      ? mixHexWithColor(branding.primaryColor, BLACK_RGB, BLACK_WEIGHT)
      : mixHexWithColor(branding.primaryColor),
    otherElementsBackground: isDark
      ? fade(
          defaultDarkOtherElements,
          Number(branding.otherElementsOpacity || 100) / 100
        )
      : fade(
          defaultLightOtherElements,
          Number(branding.otherElementsOpacity || 100) / 100
        ),
    leftMenuBackground: isDark
      ? fade(
          defaultDarkLeftMenu,
          Number(branding.leftSideMenuOpacity || 100) / 100
        )
      : fade(
          defaultLightLeftMenu,
          Number(branding.leftSideMenuOpacity || 100) / 100
        ),
    contrastText: isDark ? defaultDarkContrastText : defaultLightContrastText,
    contrastTextWrapper: isDark
      ? defaultDarkContrastTextWrapper
      : defaultLightContrastTextWrapper,
    separator: isDark ? defaultDarkSeparator : defaultLightSeparator,
    contrastIcon: isDark ? defaultDarkIcon : defaultLightIcon,
    secondaryElementsBackground: isDark
      ? defaultDarkSecondaryElementsBackground
      : defaultLightSecondaryElementsBackground,
    reverseContrastText: isDark
      ? defaultDarkReverseContrastText
      : defaultLightReverseContrastText,
    secondaryContrast: isDark
      ? defaultDarkSecondaryContrast
      : defaultLightSecondaryContrast,
    alertWarning: isDark ? defaultDarkAlertWarning : defaultLightAlertWarning,
    alertError: isDark ? defaultDarkAlertError : defaultLightAlertError,
    alertInfo: isDark ? defaultDarkAlertInfo : defaultLightAlertInfo,
    alertSuccess: isDark ? defaultDarkAlertSuccess : defaultLightAlertSuccess,
    tooltipBackground: isDark
      ? defaultDarkTooltipBackground
      : defaultLightTooltipBackground,
    tooltipText: isDark ? defaultDarkTooltipText : defaultLightTooltipText,
    tableRowTitle: isDark
      ? defaultDarkTableRowTitle
      : defaultLightTableRowTitle,
    tableRowIcon: isDark ? defaultDarkTableRowIcon : defaultLightTableRowIcon,
    attachmentsBackground: isDark
      ? defaultDarkAttachmentsBackground
      : defaultLightAttachmentsBackground,
    contrastEmailText: isDark
      ? defaultDarkContrastEmailText
      : defaultLightContrastEmailText,
    contrastEmailWrapper: isDark
      ? defaultDarkContrastEmailWrapper
      : defaultLightContrastEmailWrapper,
    emailSeparator: isDark
      ? defaultDarkEmailSeparator
      : defaultLightEmailSeparator,
    emailBackground: isDark
      ? defaultDarkOtherElements
      : defaultLightEmailBackground,
    inputLabelDisabled: isDark
      ? defaultDarkDisabledInputLabel
      : defaultLightDisabledInputLabel,
    secondaryTextDisabled: isDark
      ? defaultDarkDisabledSecondaryText
      : defaultLightDisabledSecondaryText,
    placeholder: isDark ? defaultDarkPlaceholder : defaultLightPlaceholder,
    tableDataRow: isDark ? defaultDarkTableDataRow : defaultLightTableDataRow,
    paymentName: isDark ? defaultDarkPaymentName : defaultLightPaymentName,
    autofillBackground: isDark
      ? defaultDarkAutofillBackground
      : defaultLightAutofillBackground,
    disabledButtonText: isDark
      ? defaultDarkDisabledButtonText
      : defaultLightDisabledButtonText,
    disabledButtonBackground: isDark
      ? defaultDarkDisabledButtonBackground
      : defaultLightDisabledButtonBackground,
    disabledText: isDark ? defaultDarkDisabledText : defaultLightDisabledText,
    chipBackground: isDark
      ? defaultDarkChipBackground
      : defaultLightChipBackground,
  });
};

export const useGenerateThemeFromBranding = (branding: Branding) => {
  const leftSideMenuOpacity = branding?.leftSideMenuOpacity;
  const otherElementsOpacity = branding?.otherElementsOpacity;
  const primaryColor = branding?.primaryColor;
  const brandingTheme = branding?.theme;
  return useMemo(
    () =>
      generateThemeFromBranding({
        leftSideMenuOpacity,
        otherElementsOpacity,
        primaryColor,
        theme: brandingTheme,
      } as Branding),
    [leftSideMenuOpacity, otherElementsOpacity, primaryColor, brandingTheme]
  );
};
